<template>
    <div class="max-w-(--breakpoint-sm)">
        <page-header>Cost Visibility</page-header>

        <page-loading v-if="loading"></page-loading>

        <vx-box v-else color="info">
            <div class="flex flex-col gap-6 p-8">
                <p class="text-sm text-slate-600">
                    Control who sees messaging cost information in the UI. This applies to estimated costs for quick
                    blast, campaign lists, and outbound message analytics.
                </p>

                <div class="flex items-center justify-between">
                    <div>
                        <span class="text-base font-bold text-slate-900">OUs</span>
                        <p class="text-sm text-slate-500">Display cost estimations for organizational unit users?</p>
                    </div>
                    <vx-toggle v-model="orgUnits" size="lg" data-test="org-units-toggle"></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <span class="text-base font-bold text-slate-900">Groups</span>
                        <p class="text-sm text-slate-500">Display cost estimations for group users?</p>
                    </div>
                    <vx-toggle v-model="groups" size="lg" data-test="groups-toggle"></vx-toggle>
                </div>

                <vx-button
                    @click.prevent="save"
                    type="button"
                    color="primary"
                    size="lg"
                    block
                    data-test="save"
                    :loading="saving"
                >
                    Update
                </vx-button>
            </div>
        </vx-box>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { VxBox, VxButton, VxToggle } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { teamsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const loading = ref(false);
const saving = ref(false);

const orgUnits = ref(true);
const groups = ref(true);

const fetchData = async () => {
    loading.value = true;

    try {
        const response = await teamsClient.getCostVisibility(teamId);
        orgUnits.value = response.data.org_units;
        groups.value = response.data.groups;
    } catch (e) {
        toasted.global.platform_error();
        console.error(e);
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    if (saving.value || loading.value) {
        return;
    }

    saving.value = true;

    try {
        await teamsClient.updateCostVisibility(teamId, {
            org_units: orgUnits.value,
            groups: groups.value,
        });
        toasted.global.saved_successfully();
    } catch (e) {
        toasted.global.platform_error();
        console.error(e);
    } finally {
        saving.value = false;
    }
};

fetchData();
</script>
